/**
 * Firebase Configuration and Initialization
 *
 * This module initializes Firebase app with configuration from environment variables.
 * It provides access to Firebase Auth instance for authentication operations.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, type Auth } from 'firebase/auth'

/**
 * Firebase configuration interface
 */
interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

/**
 * Get Firebase configuration from environment variables
 */
const getFirebaseConfig = (): FirebaseConfig => {
  const config: FirebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  }

  // Validate that all required config values are present
  const missingKeys = Object.entries(config)
    .filter(([_, value]) => !value)
    .map(([key]) => key)

  if (missingKeys.length > 0) {
    console.warn(
      `Firebase configuration is incomplete. Missing: ${missingKeys.join(', ')}. ` +
        'Authentication features will not work properly.'
    )
  }

  return config
}

/**
 * Firebase app instance
 */
let firebaseApp: FirebaseApp | null = null

/**
 * Firebase Auth instance
 */
let firebaseAuth: Auth | null = null

/**
 * Initialize Firebase app and auth
 * @returns Firebase app instance
 */
export const initializeFirebase = (): FirebaseApp => {
  if (firebaseApp) {
    return firebaseApp
  }

  const config = getFirebaseConfig()

  try {
    firebaseApp = initializeApp(config)
    firebaseAuth = getAuth(firebaseApp)

    console.log('Firebase initialized successfully')
  } catch (error) {
    console.error('Failed to initialize Firebase:', error)
    throw error
  }

  return firebaseApp
}

/**
 * Get Firebase Auth instance
 * @returns Firebase Auth instance
 */
export const getFirebaseAuth = (): Auth => {
  if (!firebaseAuth) {
    initializeFirebase()
  }

  if (!firebaseAuth) {
    throw new Error('Firebase Auth is not initialized')
  }

  return firebaseAuth
}

/**
 * Check if Firebase is initialized
 * @returns True if Firebase is initialized
 */
export const isFirebaseInitialized = (): boolean => {
  return firebaseApp !== null && firebaseAuth !== null
}
